import { generateId } from 'vince-core/utils/string';
import {
    type ColumnConfig,
    type ColumnsConfig,
    type ExpandedSchemaProperty,
    type InferredColumn,
    PropertyFormat,
    type Schema,
    type Table,
    UpdateType
} from 'vince-core/types/tables';
import { DataType } from 'vince-core/types/common';
import { JsonSchemaType } from 'vince-core/types/formats';
import { TABLE_COLUMN_TYPE_OPTIONS } from 'vince-core/constants/tables';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sortByDisplayOrder (entries: [string, any][], displayOrder: string[]) {
    return entries.sort(([a], [b]) => {
        const indexA = displayOrder.indexOf(a);
        const indexB = displayOrder.indexOf(b);
        // If not found in displayOrder sort alphabetically
        if (indexA === -1 && indexB === -1) {
            return a.localeCompare(b);
        }
        if (indexA === -1) { return 1; }
        if (indexB === -1) { return -1; }
        return indexA - indexB;
    });
}

export function createNewTable (): Table {
    return {
        columnConfig: {},
        primaryKeys: [],
        displayOrder: [],
        schema: {
            type: JsonSchemaType.OBJECT,
            properties: {},
            required: []
        },
        tableAlias: '',
        tableName: '',
        entity: 'META',
        rowCount: 0,
        updateType: UpdateType.UPSERT,
        timestamp: '',
        shardMax: 0
    };
}

export const inferSchema = (column: ColumnConfig): {type: JsonSchemaType, format: PropertyFormat | undefined} => {
    let type = JsonSchemaType.STRING;
    let format;
    if (column.dataType === DataType.NUMERIC) {
        type = JsonSchemaType.NUMBER;
    } else if (column.dataType === DataType.DATE) {
        type = JsonSchemaType.STRING;
        format = PropertyFormat.M3_DATE; // we assume m3 data since that is 99% likely
    }

    return { type, format };
};

// Convert column config to JSON schema format
export const convertColumnConfigToSchema = (columnConfig: ColumnsConfig): Schema => {
    const schema = {
        type: 'object',
        properties: {},
        required: []
    } as Schema;
    const keys = Object.keys(columnConfig || {});
    keys.forEach((key) => {
        const column = columnConfig[key]!;
        const { type, format } = inferSchema(column);
        schema.properties[key] = {
            type,
            title: column!.aliasName,
            format
        };
    });
    return schema;
};

// Conver JSON schema to old column config format for compatibility
export const convertSchemaToColumnConfig = (schema: Schema) => {
    const columnConfig = {} as ColumnsConfig;
    Object.keys(schema.properties || {}).forEach((key) => {
        const column = schema.properties[key];
        let dataType = DataType.STRING;
        if ([JsonSchemaType.NUMBER, JsonSchemaType.INTEGER].includes(column!.type)) {
            dataType = DataType.NUMERIC;
        } else if (column?.format?.includes('date')) {
            dataType = DataType.DATE;
        }
        columnConfig[key] = {
            aliasName: column!.title!,
            dataType
        };
    });
    return columnConfig;
};

export function getColumns (table: Table): ExpandedSchemaProperty[] {
    // TODO: Convert to schema if schema not present
    if (Object.keys(table.schema ?? {}).length === 0) {
        table.schema = convertColumnConfigToSchema(table.columnConfig);
    }
    return getColumnsFromSchema(table);
}

export function getColumnsFromSchema (table: Table): ExpandedSchemaProperty[] {
    const displayOrder = table.displayOrder || [];
    const properties = Object.entries(table.schema!.properties);
    const sortedProperties = sortByDisplayOrder(properties, displayOrder);

    const test = sortedProperties.map(([key, value]) => ({
        id: generateId(),
        ...value,
        name: key,
        selectedType: {
            type: value.type,
            ...(value.format ? { format: value.format } : {}),
            ...(value.items ? { items: value.items } : {})
        },
        primaryKey: table.primaryKeys.includes(key),
        required: table.schema!.required?.includes(key) ?? false
    }));
    return test;
}

type ColumnType = NonNullable<ExpandedSchemaProperty['selectedType']>;

export function toColumnType (type: string, format?: string): ColumnType {
    const options = TABLE_COLUMN_TYPE_OPTIONS.flatMap(group => group.items) as { value: ColumnType }[];
    const option = options.find(it => it.value.type === type && it.value.format === format) ??
        options.find(it => it.value.type === type && !it.value.format);
    return option ? { ...option.value } : { type: JsonSchemaType.STRING };
}

export function inferredColumnsToExpanded (inferred: InferredColumn[]): ExpandedSchemaProperty[] {
    return inferred.map((column) => {
        const selectedType: ColumnType = { type: column.type, ...(column.format ? { format: column.format } : {}) };
        return {
            id: generateId(),
            name: column.name,
            title: column.title,
            type: column.type,
            ...(column.format ? { format: column.format } : {}),
            selectedType,
            primaryKey: column.primaryKey,
            required: false,
            nameUpdatable: false,
            inference: column.inference
        };
    });
}

interface UploadErrorItem {
    code?: string;
    error?: string;
    row?: number;
    column?: string;
    columnLetter?: string;
    rows?: number[];
    keys?: string[];
    values?: unknown[];
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

function getUploadErrorLine (item: UploadErrorItem, t: Translate, locale: string): string {
    // Joins the items as a list in the user's language, e.g. "4, 5, and 9" or "4, 5 og 9"
    const list = (items: unknown[]) => new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items.map(String));

    // Column without a header, or with a header starting with __ (then named in column)
    if (item.code === 'propertyNames' && item.columnLetter) {
        return !item.column
            ? t('tables.uploadErrorNoHeader', { column: item.columnLetter })
            : t('tables.uploadErrorReservedHeader', { column: item.columnLetter, header: item.column });
    }
    if (item.code === 'uniqueItemCombination' && item.rows?.length && item.keys?.length) {
        const params = { rows: list(item.rows), keys: list(item.keys) };
        return item.values?.length
            ? t('tables.uploadErrorDuplicateKeyValue', { ...params, values: list(item.values) })
            : t('tables.uploadErrorDuplicateKey', params);
    }
    // Strip the JSON pointer prefix such as "/2/qty: " or "/0 "
    let message = (item.error ?? '').replace(/^\/\S*:?\s*/, '');
    let column = item.column;
    // Missing required values only name the column in the message
    const missing = /^must have required property '(.+)'$/.exec(message);
    if (missing && !column) {
        column = missing[1];
        message = t('tables.uploadErrorMissingValue');
    }
    if (item.row == null) { return message; }
    return column
        ? t('tables.uploadErrorAt', { row: item.row, column, message })
        : t('tables.uploadErrorAtRow', { row: item.row, message });
}

export function getUploadErrorLines (err: unknown, t: Translate, locale: string): string[] {
    const fetchError = err as { data?: unknown, response?: { _data?: unknown } } | undefined;
    const body = fetchError?.response?._data ?? fetchError?.data;
    const details = (typeof body === 'object' && body ? (body as { error?: unknown }).error : body);

    if (Array.isArray(details)) {
        const lines = (details as UploadErrorItem[]).map(item => getUploadErrorLine(item, t, locale)).filter(Boolean);
        if (lines.length) {
            return lines;
        }
    } else if (typeof details === 'string' && details) {
        return [details];
    }
    return [t('tables.uploadFailedGeneric')];
}
